import type {
  ID,
  Maybe,
  Response,
  StoreThis,
  User,
  UserFormType,
  UserListItem,
} from '@/types'
import type { ToastState } from '@/types/ui'

import { computed, ref } from 'vue'

import { defineStore } from 'pinia'
import { useUiStore } from '@/stores/ui'
import { useModelsStore } from '@/stores/models'

import {
  findByPropValue,
  unProxy,
  handleErrors,
  clone,
  removeFromList,
  isEmpty,
  findIndexByPropValue,
} from '@/helpers'

import UserModel from '@/models/UserModel'

type SingleType = User
type ListItemType = UserListItem
type FormType = UserFormType

const resource = 'users'
const Model = UserModel

export const useUsersStore = defineStore(resource, () => {
  const modelsStore = useModelsStore()
  const ui = useUiStore()

  const form = ref<FormType>(Model.get())
  const snapshot = ref<FormType>(Model.get())

  const items = ref<ListItemType[]>([])

  const itemsAreLoading = ref(false)
  const singleItemIsLoading = ref(false)
  const isDeleting = ref(false)

  const validation = ref<Partial<Record<keyof FormType, string[]>>>({})
  const otherError = ref<Maybe<string>>(null)

  const canSaveForm = computed(
    () =>
      !isEmpty(Model.onlyModelKeysDiff<FormType>(snapshot.value, form.value))
  )

  async function fetchItems(this: StoreThis) {
    itemsAreLoading.value = true

    try {
      const {
        data: { data, success, errors },
      }: Response<ListItemType[]> = await this.rest.get(`/m/${resource}`)

      if (!success && errors) throw Error(handleErrors(errors))

      items.value = data ?? []
    } catch (e) {
      console.error('[fetchItems]', resource, e)
    } finally {
      itemsAreLoading.value = false
    }
  }

  async function fetchCurrentUserInfo(this: StoreThis) {
    let currentUser: Maybe<SingleType> = null

    singleItemIsLoading.value = true

    try {
      const {
        data: { data, success, errors },
      }: Response<SingleType> = await this.rest.get(`/m/${resource}/info`)

      if (!success && errors) throw Error(handleErrors(errors))

      const index = findIndexByPropValue(items.value, data.id)

      if (index !== -1) {
        Model.reset<ListItemType, SingleType>(items.value[index], data)
      } else items.value.push(data)

      currentUser = data
    } catch (e) {
      console.error('[fetchCurrentUserInfo]', resource, e)
    } finally {
      singleItemIsLoading.value = false
    }

    return currentUser
  }

  async function add(this: StoreThis, payload: FormType = form.value) {
    const options: Partial<ToastState> = {}
    let id: Maybe<ID> = null
    let error = false

    singleItemIsLoading.value = true

    try {
      const {
        data: { success, errors, data },
      }: Response<SingleType> = await this.rest.post(
        `/m/${resource}/store`,
        clone(unProxy(payload))
      )

      if (!success && errors) {
        validation.value = errors.validation ?? {}
        otherError.value = errors.other ?? null

        throw Error(handleErrors(errors))
      }

      id = data.id

      options.type = 'success'
      options.text = 'Добавление прошло успешно'

      items.value.push(data)
    } catch (e) {
      error = true
      console.error('[add]', resource, e)

      options.text = 'Что-то пошло не так. Добавить не удалось'
    } finally {
      singleItemIsLoading.value = false

      if (options.text) ui.showToast(options)
    }

    return {
      error,
      id,
      otherError: otherError.value,
      validationErrors: validation.value,
    }
  }

  async function edit(this: StoreThis, id: ID, payload: FormType = form.value) {
    const options: Partial<ToastState> = {}
    let error = false

    singleItemIsLoading.value = true

    try {
      const {
        data: { success, errors, data },
      }: Response<SingleType> = await this.rest.put(
        `/m/${resource}/${id}`,
        clone(unProxy(payload))
      )

      if (!success && errors) {
        validation.value = errors.validation ?? {}
        otherError.value = errors.other ?? null

        throw Error(handleErrors(errors))
      }

      const item = getItemById(id)
      if (item) {
        Model.reset<ListItemType, SingleType>(item, data)
      }

      options.type = 'success'
      options.text = 'Изменения сохранены'
    } catch (e) {
      error = true
      console.error('[edit]', resource, e)

      options.text = 'Что-то пошло не так. Сохранить не удалось'
    } finally {
      singleItemIsLoading.value = false

      if (options.text) ui.showToast(options)
    }

    return {
      error,
      id,
      otherError: otherError.value,
      validationErrors: validation.value,
    }
  }

  async function remove(this: StoreThis, id: ID) {
    const options: Partial<ToastState> = {}
    let error = false

    isDeleting.value = true

    try {
      const {
        data: { success, errors },
      }: Response<any> = await this.rest.delete(`/m/${resource}/${id}`)

      if (!success && errors) throw Error(handleErrors(errors))

      removeFromList(items.value, id)
    } catch (e) {
      error = true
      console.error('[remove]', e)

      options.text = 'Что-то пошло не так. Удалить не удалось'
    } finally {
      isDeleting.value = false

      if (options.text) ui.showToast(options)
    }

    return error
  }

  function getItemById<T = SingleType | ListItemType>(
    id?: Maybe<ID>
  ): Maybe<T> {
    return Number.isInteger(id) ? findByPropValue(items.value, id) : null
  }

  function reset(id?: Maybe<ID>) {
    if (Number.isInteger(id)) {
      const item = (getItemById(id) ?? {}) as ListItemType

      snapshot.value = Model.mergeTogetherAndCutExcess(item)
      snapshot.value.role_name = item.roles?.[0]?.code ?? null
    } else if (id === null || id === undefined) {
      snapshot.value = Model.get()
    }

    Model.reset(form.value, snapshot.value)
    validation.value = {}
    otherError.value = null
  }

  return {
    form,
    snapshot,

    canSaveForm,
    validation,
    otherError,

    items,

    itemsAreLoading,
    singleItemIsLoading,
    isDeleting,

    fetchItems,
    fetchCurrentUserInfo,
    add,
    edit,
    remove,

    toggle: (id: ID) => modelsStore.toggle(id, 'user'),

    getItemById,
    reset,
  }
})
